import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultProductResultLotData, PRD3030_R01ProductResultLotDto } from './Dto_PRD3030_R01';
import { usePRD3030_R01Store } from './Store_PRD3030_R01';

const LOT_COLUMN_STATE_KEY = 'PRD3030_R01-lot-grid-column-state';

const BOTTOM_LABEL_COLS = new Set(['lotNo']);

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		const prevCellStyle = def.cellStyle;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) => {
				if (params?.node?.rowPinned === 'bottom') {
					return { textAlign: 'center' };
				}
				return typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle;
			},
		};
	});

/**
 * LOT 조회 테이블 컴포넌트
 * 생산실적번호로 생산입고된 LOT 목록을 표시
 */
export const LotTable_PRD3030_R01 = () => {
	const gridRef = useRef<AgGridReact<PRD3030_R01ProductResultLotDto>>(null);

	const {
		lotList,
		selectedLot,
		setSelectedLot,
		initSelectedPRD3030_R01List,
		lotColumnState,
		setLotColumnState,
		lotScrollTop, setLotScrollTop,
	} = usePRD3030_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'lotNo',
		gridData: lotList,
		defaultGridData: defaultProductResultLotData,
		selectedRow: selectedLot,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		lotNo: 'Lot No',
		lotQty: '생산수량',
	};

	const baseColumnDefs: ColDef<PRD3030_R01ProductResultLotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 200,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '생산수량', colId: 'lotQty', field: 'lotQty', width: 120,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PRD3030_R01ProductResultLotDto>[] = useMemo(() => {
		const merged = lotColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs<PRD3030_R01ProductResultLotDto>({
				baseColumnDefs,
				columnState: lotColumnState,
				headerNames,
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, BOTTOM_LABEL_COLS);
	}, [lotColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => ([{
		lotNo: `${lotList.length.toLocaleString()}건`,
		lotQty: lotList.reduce((sum, item) => sum + (item.lotQty || 0), 0),
	}]), [lotList]);

	const handleLotRowClick = useCallback((e: RowClickedEvent<PRD3030_R01ProductResultLotDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		setSelectedLot(rowData);
	}, [setSelectedLot]);

	return (
		<div className="w-full h-full flex flex-col">
			<div className="flex-1">
				<GridContextWrapper<PRD3030_R01ProductResultLotDto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					initSelected={initSelectedPRD3030_R01List}
					defaultRowData={defaultProductResultLotData}
					baseColumnDefs={lotColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setLotColumnState}
					localStorageKey={LOT_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<PRD3030_R01ProductResultLotDto>
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						onCellKeyDown={undefined}
						rowClassRules={createRowClassRules<PRD3030_R01ProductResultLotDto>({
							idKey: 'valueIndex',
							selectedList: selectedLot?.valueIndex ? [selectedLot.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleLotRowClick}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={LOT_COLUMN_STATE_KEY}
						setColumnState={setLotColumnState}
						scrollTop={lotScrollTop}
						setScrollTop={setLotScrollTop}
						pinnedBottomRowData={pinnedBottomRow}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
